import { WelcomePageGuard } from 'new-components/WelcomePageGuard/WelcomePageGuard';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { getOpParams } from 'utils/getOpParams';
import { VISITOR_NATIVE_APP_ID, NativeAppStatus } from 'utils/nativeApps';
import { useFeatureFlag } from 'utils/customHooks/useFeatureFlag';
import { useFeatureGate } from 'utils/customHooks/useFeatureGate';

import { visitorWelcomeRoute } from './constants/routes';

/**
 * @description Checks if the org has the Visitor native app and if it is enabled.
 * If it is it outlets to the page the user navigated too otherwise it goes to the welcome page
 * for Visitor to upsell
 */
export const VisitorWelcomePageGuard = () => {
  const { orgId } = getOpParams();

  const { data: visitorNativeApp, isLoading: isNativeAppLoading } = useOpQuery({
    apiEndpointName: 'listOrgNativeApps',
    parameters: [orgId],
    select: (heliumResponse) =>
      heliumResponse.json.data.find(
        (app) => app.nativeAppId === VISITOR_NATIVE_APP_ID,
      ),
  });
  const { value: isVisitorEnabledFF, isPending: isFeatureFlagPending } =
    useFeatureFlag<boolean>('ENABLE_VISITOR', orgId);

  // Visitor is a tiered feature (no single 'visitor' code exists in Helium),
  // so the org has Visitor if it has ANY of the tier features.
  const { hasFeature: hasVisitorSmall, isPending: isSmallPending } =
    useFeatureGate('visitorSmall', orgId);
  const { hasFeature: hasVisitorMedium, isPending: isMediumPending } =
    useFeatureGate('visitorMedium', orgId);
  const { hasFeature: hasVisitorLarge, isPending: isLargePending } =
    useFeatureGate('visitorLarge', orgId);

  const hasVisitorFeature =
    hasVisitorSmall || hasVisitorMedium || hasVisitorLarge;
  const isFeaturePending = isSmallPending || isMediumPending || isLargePending;

  const shouldShowWelcomePage = Boolean(
    !isVisitorEnabledFF &&
      !hasVisitorFeature &&
      !(visitorNativeApp && visitorNativeApp.status === NativeAppStatus.Active),
  );

  return (
    <WelcomePageGuard
      shouldShowWelcomePage={shouldShowWelcomePage}
      isLoading={isFeatureFlagPending || isNativeAppLoading || isFeaturePending}
      welcomePagePath={visitorWelcomeRoute}
    />
  );
};
